import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { CalendarIcon, Timer, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Project } from "@/types/Project";

interface TimeEntryFormProps {
  onSubmit: (entry: TimeEntry) => void;
  projects: Project[];
}

export interface TimeEntry {
  id: string;
  date: Date;
  minutes: number;
  projectId?: string;
  description: string;
  is_billable?: boolean;
}

export function TimeEntryForm({ onSubmit, projects }: TimeEntryFormProps) {
  const [date, setDate] = useState<Date>(new Date());
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [projectId, setProjectId] = useState<string>();
  const [description, setDescription] = useState("");
  const [isBillable, setIsBillable] = useState(true);
  const [error, setError] = useState<string>();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);

    const totalMinutes = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    
    if (totalMinutes === 0) {
      setError("Please enter time spent");
      return;
    }

    if (!description.trim()) {
      setError("Please enter a description");
      return;
    }

    const entry: TimeEntry = {
      id: crypto.randomUUID(),
      date,
      minutes: totalMinutes,
      projectId,
      description: description.trim(),
      is_billable: isBillable,
    };

    onSubmit(entry);
    setHours("");
    setMinutes("");
    setProjectId(undefined);
    setDescription("");
    setIsBillable(true);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 animate-fade-up">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(date) => date && setDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Time Spent</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="number"
                min="0"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="pl-9"
                placeholder="Hours"
              />
              <Timer className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
            <div className="relative flex-1">
              <Input
                type="number"
                min="0"
                max="59"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="Minutes"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Project</label>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger>
            <SelectValue placeholder="Select a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <div className="relative">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[100px] pl-9"
            placeholder="What did you work on?"
            required
          />
          <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="billable"
          checked={isBillable}
          onChange={(e) => setIsBillable(e.target.checked)}
          className="h-4 w-4 rounded border-input cursor-pointer"
        />
        <label htmlFor="billable" className="text-sm font-medium cursor-pointer">
          Billable time entry
        </label>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <Button type="submit" className="w-full">
        Add Time Entry
      </Button>
    </form>
  );
}